<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    class="university-list-dialog"
  >
    <div v-loading="loading" class="dialog-content">
      <!-- 大学列表 -->
      <div v-if="universityList.length > 0" class="university-list">
        <div 
          v-for="university in universityList" 
          :key="university.id"
          class="university-item"
          @click="handleUniversityClick(university.id)"
        >
          <div class="university-header">
            <div class="university-name-section">
              <h4 class="university-name">
                {{ university.name }}
                <span v-if="university.english_name" class="university-english-name">
                  {{ university.english_name }}
                </span>
              </h4>
            </div>
            <div v-if="university.rank" class="university-rank">
              <el-tag type="warning" size="small">排名: {{ university.rank }}</el-tag>
            </div>
          </div>
          
          <div class="university-info">
            <div class="info-row">
              <span class="info-label">
                <i class="el-icon-location"></i> 地区：
              </span>
              <span class="info-value">{{ university.area || '未知' }}</span>
            </div>
            
            <div class="info-row">
              <span class="info-label">
                <i class="el-icon-school"></i> 类型：
              </span>
              <span class="info-value">{{ university.type || '未知' }}</span>
            </div>
            
            <div v-if="university.tag" class="info-row">
              <span class="info-label">
                <i class="el-icon-collection-tag"></i> 标签：
              </span>
              <div class="tag-list">
                <el-tag 
                  v-for="(tag, index) in parseTags(university.tag)" 
                  :key="index"
                  size="small"
                  type="info"
                  class="tag-item"
                >
                  {{ tag }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-else-if="!loading" class="empty-state">
        <i class="el-icon-info"></i>
        <p>暂无开设该专业的大学信息</p>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import request from '@/http/request'

export default {
  name: 'UniversityListDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subId: {
      type: Number,
      default: null
    },
    majorName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      universityList: []
    }
  },
  computed: {
    dialogTitle() {
      const majorName = this.majorName || '该专业'
      const count = this.universityList.length
      return `开设${majorName}的大学（共${count}所）`
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal && this.subId) {
        this.fetchUniversityList()
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit('update:visible', false)
      }
    }
  },
  methods: {
    async fetchUniversityList() {
      if (!this.subId) {
        this.$message.warning('专业ID不能为空')
        return
      }
      
      this.loading = true
      this.universityList = []
      
      try {
        const result = await request(
          '/api/Subspecialty/getUniversityBySub',
          { sub_id: this.subId },
          'get'
        )
        
        // request 函数已经返回了 result.result，所以这里直接使用
        if (Array.isArray(result)) {
          this.universityList = result
          if (result.length === 0) {
            this.$message.info('暂无开设该专业的大学信息')
          }
        } else {
          this.$message.warning('返回数据格式不正确')
        }
      } catch (error) {
        console.error('获取大学列表失败:', error)
        this.$message.error(`获取大学列表失败: ${error.message || '未知错误'}`)
        this.universityList = []
      } finally {
        this.loading = false
      }
    },
    
    parseTags(tagString) {
      if (!tagString) return []
      return tagString.split(',').map(tag => tag.trim()).filter(tag => tag)
    },
    
    handleClose() {
      this.dialogVisible = false
      this.$emit('update:visible', false)
    },
    
    handleUniversityClick(universityId) {
      // 跳转到大学详情页面
      this.$router.push({
        path: '/radar/university',
        query: {
          id: universityId
        }
      })
      // 关闭当前对话框
      this.handleClose()
    }
  }
}
</script>

<style lang="scss" scoped>
.university-list-dialog {
  .dialog-content {
    min-height: 200px;
    max-height: 600px;
    overflow-y: auto;
  }
  
  .university-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  .university-item {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    transition: all 0.3s;
    cursor: pointer;
    
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border-color: #3b82f6;
      transform: translateY(-2px);
    }
  }
  
  .university-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .university-name-section {
    flex: 1;
  }
  
  .university-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .university-english-name {
    font-size: 13px;
    color: #64748b;
    font-style: italic;
    font-weight: normal;
  }
  
  .university-rank {
    margin-left: 12px;
  }
  
  .university-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .info-row {
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    
    .info-label {
      color: #475569;
      font-weight: 500;
      min-width: 80px;
      display: flex;
      align-items: center;
      
      i {
        margin-right: 4px;
        color: #3b82f6;
      }
    }
    
    .info-value {
      color: #1e293b;
      flex: 1;
    }
  }
  
  .tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
    
    .tag-item {
      margin: 0;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #94a3b8;
    
    i {
      font-size: 48px;
      margin-bottom: 16px;
      display: block;
      color: #cbd5e1;
    }
    
    p {
      font-size: 14px;
      margin: 0;
    }
  }
}

.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>

